import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import AdminSidebar from '../components/AdminSidebar';
import PassagePicker from '../components/PassagePicker';
import DashboardHeader from '../components/DashboardHeader';
import { useAutosizeTextarea } from '../hooks/useAutosizeTextarea';
import { passageIdFromLocationSearch } from '../utils/passageUrl';
import { formatChinesePunctuation } from '../utils/chinesePunctuation';
import './Dashboard.css';

const LINE_TEXTAREA_STYLE = {
  fontFamily: 'ui-monospace, monospace',
  fontSize: 14,
  lineHeight: 1.5,
};

const MIN_TEXTAREA_HEIGHT_PX = 120;

function mapCursorToFormatted(selectionStart, endPos) {
  const s = Math.max(0, Math.min(selectionStart, endPos.length - 1));
  return endPos[s];
}

const ShortSentenceRow = ({
  sentenceId,
  title,
  text,
  onChange,
  punctMode,
  disabled,
}) => {
  const taRef = useRef(null);

  const sharedStyle = {
    ...LINE_TEXTAREA_STYLE,
    width: '100%',
    boxSizing: 'border-box',
    overflow: 'hidden',
    resize: 'none',
    minHeight: MIN_TEXTAREA_HEIGHT_PX,
    minWidth: 0,
  };

  const measure = useCallback(() => {
    const el = taRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const h = Math.max(el.scrollHeight, MIN_TEXTAREA_HEIGHT_PX);
    const nextHeight = `${h}px`;
    if (el.style.height !== nextHeight) {
      el.style.height = nextHeight;
    }
    el.style.overflow = 'hidden';
  }, []);

  useAutosizeTextarea(measure, [text]);

  const handleChange = (e) => {
    const raw = e.target.value;
    const sel = e.target.selectionStart;
    if (!punctMode) {
      onChange(raw);
      return;
    }
    const { formatted, endPos } = formatChinesePunctuation(raw);
    if (formatted === raw) {
      onChange(raw);
      return;
    }
    onChange(formatted);
    requestAnimationFrame(() => {
      const el = taRef.current;
      if (!el) return;
      const next = mapCursorToFormatted(sel, endPos);
      el.setSelectionRange(next, next);
    });
  };

  return (
    <div style={{ marginBottom: 24, paddingBottom: 20, borderBottom: '1px solid #e0e0e0' }}>
      <label htmlFor={`short-sentence-${sentenceId}`} style={{ display: 'block', margin: '0 0 8px', fontSize: 16, fontWeight: 600 }}>
        {title}
      </label>
      <textarea
        ref={taRef}
        id={`short-sentence-${sentenceId}`}
        value={text}
        rows={1}
        disabled={disabled}
        onChange={handleChange}
        style={sharedStyle}
      />
    </div>
  );
};

const ShortSentence = () => {
  const navigate = useNavigate();
  const [passages, setPassages] = useState([]);
  const [passagesLoading, setPassagesLoading] = useState(true);
  const [passagesError, setPassagesError] = useState('');
  const [passageId, setPassageId] = useState(passageIdFromLocationSearch);
  const [hasShortSentences, setHasShortSentences] = useState(false);
  const [sentenceIds, setSentenceIds] = useState([]);
  const [textBySentenceId, setTextBySentenceId] = useState({});
  const [punctMode, setPunctMode] = useState(false);
  const [loadLoading, setLoadLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [submitLoading, setSubmitLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setPassagesLoading(true);
      setPassagesError('');
      try {
        const res = await api.get('/data/passages', { params: { all: '1' } });
        if (!cancelled) {
          setPassages(res.data.passages || []);
          setPassagesError('');
        }
      } catch (err) {
        if (!cancelled) {
          setPassagesError(
            err.response?.data?.error || err.response?.data?.details || 'Failed to load passages'
          );
          setPassages([]);
        }
      } finally {
        if (!cancelled) setPassagesLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!passageId) {
      setSentenceIds([]);
      setTextBySentenceId({});
      setHasShortSentences(false);
      setPunctMode(false);
      setLoadError('');
      return;
    }

    let cancelled = false;
    const load = async () => {
      setLoadLoading(true);
      setLoadError('');
      setMessage({ type: '', text: '' });
      try {
        const pid = parseInt(passageId, 10);
        const res = await api.get('/data/short-sentences/editor', { params: { passageId: pid } });
        if (cancelled) return;

        const list = res.data?.sentences || [];
        const hasSs = Boolean(res.data?.hasShortSentences);
        setHasShortSentences(hasSs);
        setPunctMode(!hasSs);

        const ids = list.map((s) => s.sentence_id);
        setSentenceIds(ids);

        const next = {};
        for (const s of list) {
          if (hasSs && Array.isArray(s.lines)) {
            next[s.sentence_id] = s.lines.join('\n');
          } else {
            const raw = s.value == null ? '' : String(s.value);
            const { formatted } = formatChinesePunctuation(raw);
            next[s.sentence_id] = formatted;
          }
        }
        setTextBySentenceId(next);
        setLoadError('');
      } catch (err) {
        if (!cancelled) {
          setSentenceIds([]);
          setTextBySentenceId({});
          setLoadError(
            err.response?.data?.error || err.response?.data?.details || 'Failed to load editor data'
          );
        }
      } finally {
        if (!cancelled) setLoadLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [passageId]);

  const updateText = useCallback((sentenceId, text) => {
    setTextBySentenceId((prev) => ({ ...prev, [sentenceId]: text }));
  }, []);

  const performSaveShortSentences = async () => {
    setMessage({ type: '', text: '' });
    if (!passageId || sentenceIds.length === 0) {
      setMessage({ type: 'error', text: 'Select a passage with at least one sentence.' });
      return false;
    }

    const items = sentenceIds.map((sid) => ({
      sentenceId: sid,
      text: textBySentenceId[sid] == null ? '' : String(textBySentenceId[sid]),
    }));

    setSubmitLoading(true);
    try {
      const res = await api.post('/data/short-sentences/save-for-passage', {
        passageId: parseInt(passageId, 10),
        items,
      });
      const n = res.data?.inserted;
      const kept = res.data?.kept;
      const wordsCleared = res.data?.wordsCleared || 0;
      let detail = '';
      if (n != null) {
        detail = `${n} short sentence row(s)`;
        if (kept != null) detail += `, ${n - kept} new or changed`;
        if (wordsCleared > 0) {
          detail += ` — ${wordsCleared} word row(s) on changed/removed lines were cleared, redo them on the Word page`;
        }
      }
      setMessage({
        type: 'success',
        text: detail
          ? `${res.data?.message || 'Saved'} (${detail})`
          : res.data?.message || 'Saved',
      });
      setHasShortSentences(true);
      setPunctMode(false);
      return true;
    } catch (err) {
      setMessage({
        type: 'error',
        text:
          err.response?.data?.error ||
          err.response?.data?.details ||
          'Failed to save short sentences',
      });
      return false;
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    await performSaveShortSentences();
  };

  const handleSaveAndGoWord = async () => {
    const ok = await performSaveShortSentences();
    if (ok && passageId) navigate(`/word?passageId=${encodeURIComponent(passageId)}`);
  };

  const hasRows = sentenceIds.length > 0;

  return (
    <div className="dashboard">
      <DashboardHeader title="Short sentence editor" />

      <div className="dashboard-layout">
        <AdminSidebar />
        <div className="dashboard-content" style={{ maxWidth: 1100, margin: '0 auto' }}>
          {message.text && <div className={`message ${message.type}`}>{message.text}</div>}

          <div className="form-section">
            <h2>Short sentences</h2>

            <div className="form-group">
              <PassagePicker
                inputId="short-sentence-passage-picker"
                label={
                  <>
                    Passage <span className="required">*</span>
                  </>
                }
                passages={passages}
                passagesLoading={passagesLoading}
                passagesError={passagesError}
                value={passageId}
                onChange={setPassageId}
              />
            </div>

            {passageId && loadLoading && <div className="loading-state">Loading…</div>}
            {passageId && !loadLoading && loadError && <div className="message error">{loadError}</div>}

            {passageId && !loadLoading && hasRows && (
              <form onSubmit={handleSave}>
                <p className="form-hint" style={{ marginBottom: 16 }}>
                  {hasShortSentences
                    ? 'Each block is one sentence; one line per short fragment. Order follows sentence.order and short_sentence.order.'
                    : 'One textarea per sentence from the sentence table. Chinese punctuation (，。︰；！？：) starts a new line as you type.'}
                </p>

                {sentenceIds.map((sid, index) => (
                  <ShortSentenceRow
                    key={sid}
                    sentenceId={sid}
                    title={`Sentence ${index + 1}`}
                    text={textBySentenceId[sid] ?? ''}
                    onChange={(t) => updateText(sid, t)}
                    punctMode={punctMode}
                    disabled={submitLoading}
                  />
                ))}

                <div style={{ marginTop: 16, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <button type="submit" className="submit-button" disabled={submitLoading}>
                    {submitLoading
                      ? 'Saving…'
                      : hasShortSentences
                        ? 'Save short sentences'
                        : 'Insert short sentences'}
                  </button>
                  <button
                    type="button"
                    className="submit-button"
                    disabled={submitLoading}
                    onClick={handleSaveAndGoWord}
                  >
                    {submitLoading
                      ? 'Saving…'
                      : hasShortSentences
                        ? 'Save short sentences and go to Word'
                        : 'Insert short sentences and go to Word'}
                  </button>
                </div>
              </form>
            )}

            {passageId && !loadLoading && !loadError && !hasRows && (
              <div className="message error">No sentences for this passage.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShortSentence;
